'use client';

import dynamic from 'next/dynamic';
import Button from '@alltrails/denali/components/Button';
import { FormattedMessage } from 'react-intl';
import useUser from '@alltrails/context/hooks/useUser';
import { useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import type TrailDetails from '@alltrails/core/types/TrailDetails';
import useInstallPrompt from '@alltrails/install-prompt/hooks/useInstallPrompt';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useRoadblock from '@alltrails/layout/hooks/useRoadblock';
import { TrailPage, getTrailShareObject } from '@alltrails/trails';
import logTrailDetailsShareThisTrailOptionClicked from '@alltrails/amplitude/events/logTrailDetailsShareThisTrailOptionClicked';
import TrailDetailsShareOption from '@alltrails/amplitude/enums/TrailDetailsShareOption';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import styles from './MobileFooter.module.scss';
import useIsTrailMapOpen from '@/hooks/useIsTrailMapOpen';

const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const InstallInterstitial = dynamic(() => import('@alltrails/install-prompt/components/InstallInterstitial'), { ssr: false });
const ShareModal = dynamic(() => import('@alltrails/sharing/components/ShareModal/ShareModal'), { ssr: false });

type Props = {
  trailDetails: TrailDetails;
  trailPage: TrailPage;
  isScrollLimitActive?: boolean;
};

const reduxListsAnalyticsInfo = { detailedCardLocation: 'mobile web trail details cta bar' as CardLocation, page: PageStrings.TRAIL_PAGE };

// https://www.figma.com/design/oevB4bXh8KLLDe1Yp8rBW2/Trail-Page-Web-%5BHandoff%5D?node-id=15304-253213&m=dev
export default function MobileFooter({ isScrollLimitActive, trailDetails, trailPage }: Props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isInterstitialOpen, setIsInterstitialOpen] = useInstallPrompt('interstitial', false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const { openTrailMap } = useIsTrailMapOpen();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { listModal, signUpModal } = useReduxListItems(undefined, false /* preventAutoInitialize */, reduxListsAnalyticsInfo);

  const onRoadblock = useRoadblock({
    afterRoadblock: openTrailMap,
    eventName: 'trail_explore',
    languageRegionCode,
    returnToUrl: `${pathname}?${searchParams}`,
    user,
    setIsInterstitialOpen
  });

  return (
    <div className={styles.footer}>
      <Button
        onClick={() => {
          logTrailDetailsActionTapped({
            trail_id: trailDetails.id.toString(),
            trail_page_action: 'download overflow menu button',
            trail_page_location: 'mobile cta bar'
          });
          setIsDownloadModalOpen(true);
        }}
        text={<FormattedMessage defaultMessage="Download" />}
        testId="download"
        variant="accent"
      />
      <Button
        onClick={() => {
          logTrailDetailsActionTapped({
            trail_id: trailDetails.id.toString(),
            trail_page_action: 'open map',
            trail_page_location: 'mobile cta bar'
          });
          if (!user) {
            setIsSignUpModalOpen(true);
          } else {
            onRoadblock();
          }
        }}
        text={<FormattedMessage defaultMessage="Open map" />}
        testId="open-map"
      />
      {isDownloadModalOpen && (
        <HitTheTrailModal
          isOpen={isDownloadModalOpen}
          onCloseRequest={() => {
            setIsDownloadModalOpen(false);
          }}
          title={<FormattedMessage defaultMessage="Download" />}
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}
      {isSignUpModalOpen && (
        <SignUpModal
          closeModal={() => setIsSignUpModalOpen(false)}
          deeplinkData={{ pageId: trailDetails.id, pageType: 'trail' }}
          isOpen={isSignUpModalOpen}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            openTrailMap();
          }}
          trigger="Hit the Trail Card Open Map"
          upsellTrigger="Hit the Trail Card Open Map"
        />
      )}
      {isInterstitialOpen && (
        <InstallInterstitial
          deeplinkData={{ pageId: trailDetails.id, pageType: 'trail' }}
          isOpen={isInterstitialOpen}
          onRequestClose={() => setIsInterstitialOpen(false)}
          promptLocation="trail page"
          isScrollLimitActive={isScrollLimitActive}
          variant="generic"
        />
      )}
      {isShareModalOpen && (
        <ShareModal
          isOpen
          onClose={() => setIsShareModalOpen(false)}
          onShareOptionClick={(shareOption: TrailDetailsShareOption) => {
            logTrailDetailsShareThisTrailOptionClicked({ share_option_clicked: shareOption, trail_id: trailDetails.id.toString() });
          }}
          shareObject={getTrailShareObject(trailDetails)}
        />
      )}
      {listModal}
      {signUpModal}
    </div>
  );
}
